/// <reference lib="dom" />
import React, { useState, useEffect } from 'react';
import type { User } from 'shared/types.js';
import { api } from './api.js';
import { Login, FirstUserSetup } from './Auth.js';
import { InvoiceSearch } from './InvoiceSearch.js';
import { InventoryLookup } from './InventoryLookup.js';
import { OutstandingTracker } from './OutstandingTracker.js';
import { Settings } from './Settings.js';
import { Reports } from './Reports.js';
import { HhStatements } from './HhStatements.js';
import { PasswordRequirements, validatePassword } from './common.js';

const getDeepLinkParams = () => {
    const params = new URLSearchParams(window.location.search);
    const invoiceNo = params.get('invoice') || params.get('doc_no') || '';
    const itemCode = params.get('item') || params.get('sku') || '';
    const requestedTab = params.get('tab');

    return {
        invoiceNo: invoiceNo.trim(),
        itemCode: itemCode.trim(),
        requestedTab,
    };
};

const ForcePasswordChangeModal = ({ onPasswordChanged }: { onPasswordChanged: () => void }) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        const passwordValidation = validatePassword(password);
        if (!passwordValidation.hasMinLength || !passwordValidation.hasNumber || !passwordValidation.hasUpperCase) {
            setError('Password does not meet the requirements.');
            return;
        }

        setIsLoading(true);
        try {
            await api.put('/api/me/password', { password });
            window.alert('Password updated successfully.');
            onPasswordChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="modal-backdrop">
            <div className="modal" style={{ width: '500px' }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <strong>Update Your Password</strong>
                </div>
                <div className="modal-body permissions">
                    <p style={{ color: 'var(--text-muted-color)', marginBottom: '1.5rem', textAlign: 'center' }}>
                        For security reasons, you are required to change your password before you can continue.
                    </p>
                    <form className="login-form" onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="new-password">New Password</label>
                            <input id="new-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
                             <PasswordRequirements password={password} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="confirm-password">Confirm New Password</label>
                            <input id="confirm-password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
                        </div>
                        <button type="submit" disabled={isLoading}>{isLoading ? 'Updating...' : 'Update Password'}</button>
                        {error && <p className="login-error">Access Denied. Please see server admin for assistance.</p>}
                    </form>
                </div>
            </div>
        </div>
    );
};

const ThemeToggle = ({ theme, onChange }: { theme: 'light' | 'dark', onChange: (theme: 'light' | 'dark') => void }) => {
    const isDark = theme === 'dark';
    const handleChange = () => {
        onChange(isDark ? 'light' : 'dark');
    };
    return (
        <button
            onClick={handleChange}
            className="theme-toggle"
            title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
            aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
        >
            <span className="theme-toggle-handle">
                {isDark ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 2a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5A.75.75 0 0110 2zM5.204 5.204a.75.75 0 011.06 0l1.061 1.06a.75.75 0 01-1.06 1.06l-1.06-1.061a.75.75 0 010-1.06zM2 10a.75.75 0 01.75-.75h1.5a.75.75 0 010 1.5h-1.5A.75.75 0 012 10zM5.204 14.796a.75.75 0 010-1.06l1.06-1.061a.75.75 0 111.06 1.06l-1.06 1.06a.75.75 0 01-1.06 0zM10 18a.75.75 0 01-.75-.75v-1.5a.75.75 0 011.5 0v1.5A.75.75 0 0110 18zM14.796 14.796a.75.75 0 01-1.06 0l-1.061-1.06a.75.75 0 111.06-1.06l1.06 1.061a.75.75 0 010 1.06zM18 10a.75.75 0 01-.75.75h-1.5a.75.75 0 010-1.5h1.5A.75.75 0 0118 10zM14.796 5.204a.75.75 0 010 1.06l-1.06 1.061a.75.75 0 01-1.06-1.06l1.06-1.06a.75.75 0 011.06 0zM10 5a5 5 0 100 10 5 5 0 000-10z" />
                    </svg>
                ) : (
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
						<path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
					</svg>
                )}
            </span>
        </button>
    );
};


export const App = () => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [appState, setAppState] = useState<'loading' | 'needs_setup' | 'login' | 'authenticated'>('loading');
    const [activeTab, setActiveTab] = useState<'search' | 'inventory' | 'tracker' | 'statements' | 'settings' | 'reports'>('search');
    const [theme, setTheme] = useState<'light' | 'dark'>('light');
    const [isForcePasswordChangeModalOpen, setIsForcePasswordChangeModalOpen] = useState(false);

    const applyTheme = (themeValue: string | undefined) => {
        if (themeValue === 'dark') {
            setTheme('dark');
            document.documentElement.classList.add('dark');
        } else {
            setTheme('light');
            document.documentElement.classList.remove('dark');
        }
    };
    
    const handleLoginSuccess = (user: User) => {
        setCurrentUser(user);
        applyTheme(user.permissions?.ui_theme);
        const canAccessTracker = user.permissions?.can_access_tracker === 'true';
        const canAccessInventoryLookup = user.permissions?.can_access_inventory_lookup === 'true';
        const { invoiceNo, itemCode, requestedTab } = getDeepLinkParams();

        if (itemCode && canAccessInventoryLookup) {
            setActiveTab('inventory');
        } else if (invoiceNo) {
            setActiveTab('search');
        } else if (requestedTab === 'inventory' && canAccessInventoryLookup) {
            setActiveTab('inventory');
        } else if (requestedTab === 'search') {
            setActiveTab('search');
        } else {
            setActiveTab(canAccessTracker ? 'tracker' : 'search');
        }
        if (user.force_password_change) {
            setIsForcePasswordChangeModalOpen(true);
        }
        setAppState('authenticated');
    };

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const { userCount } = await api.get<{userCount: number}>('/api/setup/status');
                if (userCount === 0) {
                    setAppState('needs_setup');
                    return;
                }
                
                const user = await api.get<User>('/api/me');
                handleLoginSuccess(user);
            } catch (error) {
                setAppState('login');
            }
        };
        checkAuth();
    }, []);

    useEffect(() => {
        if (appState === 'authenticated' && currentUser) {
            const canAccessTracker = currentUser.permissions?.can_access_tracker === 'true';
            const canAccessSettings = currentUser.permissions?.can_access_settings === 'true';
            const canAccessUserManagement = currentUser.permissions?.can_access_user_management === 'true';
            const canAccessInventoryLookup = currentUser.permissions?.can_access_inventory_lookup === 'true';
            const showSettingsTab = canAccessSettings || canAccessUserManagement;
            if (
                (activeTab === 'inventory' && !canAccessInventoryLookup) ||
                (activeTab === 'tracker' && !canAccessTracker) ||
                (activeTab === 'statements' && !canAccessTracker) ||
                (activeTab === 'reports' && !canAccessTracker) ||
                (activeTab === 'settings' && !showSettingsTab)
            ) {
                setActiveTab('search');
            }
        }
    }, [appState, currentUser, activeTab]);

    const handleLogout = async () => {
        try {
            await api.post('/api/logout', {});
            setCurrentUser(null);
            setAppState('login');
        } catch (error) {
            window.alert('Failed to log out.');
        }
    };

    const refreshCurrentUser = async () => {
        try {
            const user = await api.get<User>('/api/me');
            setCurrentUser(user);
        } catch (error) {
            handleLogout();
        }
    };

    const handleThemeChange = async (newTheme: 'light' | 'dark') => {
        const oldTheme = theme;
        applyTheme(newTheme);
        try {
            await api.put('/api/me/theme', { theme: newTheme });
            setCurrentUser(prevUser => {
                if (!prevUser) return null;
                return {
                    ...prevUser,
                    permissions: { ...prevUser.permissions, ui_theme: newTheme }
                };
            });
        } catch (error) {
            window.alert('Could not save your theme preference. It will be reset on your next visit.');
            applyTheme(oldTheme);
        }
    };
    
    if (appState === 'loading') {
        return <div className="login-page-container">Loading...</div>;
    }
    if (appState === 'needs_setup') {
        return <FirstUserSetup onSetupComplete={handleLoginSuccess} />;
    }
    if (appState === 'login') {
        return <Login onLoginSuccess={handleLoginSuccess} />;
    }
    if (appState === 'authenticated' && currentUser) {
        const canAccessTracker = currentUser.permissions?.can_access_tracker === 'true';
        const canAccessSettings = currentUser.permissions?.can_access_settings === 'true';
        const canAccessUserManagement = currentUser.permissions?.can_access_user_management === 'true';
        const canAccessInventoryLookup = currentUser.permissions?.can_access_inventory_lookup === 'true';
        const showSettingsTab = canAccessSettings || canAccessUserManagement;
        const { invoiceNo, itemCode } = getDeepLinkParams();
        return (
            <>
                {isForcePasswordChangeModalOpen && (
                    <ForcePasswordChangeModal
                        onPasswordChanged={() => {
                            setIsForcePasswordChangeModalOpen(false);
                            setCurrentUser(u => u ? { ...u, force_password_change: false } : null);
                        }}
                    />
                )}
                <div className="app-container">
                    <header className="app-header">
                        <div className="header-main">
                            <h1>Metro Lookup Utility</h1>
                            <nav className="app-nav">
                                <button className={`nav-button ${activeTab === 'search' ? 'active' : ''}`} onClick={() => setActiveTab('search')}>Invoice Search</button>
                                {canAccessInventoryLookup && <button className={`nav-button ${activeTab === 'inventory' ? 'active' : ''}`} onClick={() => setActiveTab('inventory')}>Inventory Lookup</button>}
                                {canAccessTracker && <button className={`nav-button ${activeTab === 'tracker' ? 'active' : ''}`} onClick={() => setActiveTab('tracker')}>HH Invoice Tracker</button>}
                                {canAccessTracker && <button className={`nav-button ${activeTab === 'statements' ? 'active' : ''}`} onClick={() => setActiveTab('statements')}>HH Statements</button>}
                                {canAccessTracker && <button className={`nav-button ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}>Reports</button>}
                                {showSettingsTab && <button className={`nav-button ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>Settings</button>}
                            </nav>
                        </div>
                        <div className="header-user-info">
                            <span>Welcome, <strong>{currentUser.name || currentUser.username}</strong></span>
                            <ThemeToggle theme={theme} onChange={handleThemeChange} />
                            <button className="logout-button" onClick={handleLogout}>Log Out</button>
                        </div>
                    </header>
                    {activeTab === 'search' && <InvoiceSearch user={currentUser} initialInvoiceNo={invoiceNo} />}
                    {activeTab === 'inventory' && canAccessInventoryLookup && <InventoryLookup initialItemCode={itemCode} />}
                    {activeTab === 'tracker' && canAccessTracker && <OutstandingTracker user={currentUser} />}
                    {activeTab === 'statements' && canAccessTracker && <HhStatements user={currentUser} />}
                    {activeTab === 'reports' && canAccessTracker && <Reports user={currentUser} />}
                    {activeTab === 'settings' && showSettingsTab && <Settings user={currentUser} onPermissionsChange={refreshCurrentUser} />}
                </div>
            </>
        );
    }
    return null;
};
